import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { parsePaging } from '../../utils/queryString';
import useExtendedQuery from '../../hooks/useExtendedQuery';
import Spinner from '../Spinner';
import Paginator from '../Paginator';
import {
    ALL_HOLIDAYS,
    ON_HOLIDAY_CHANGED,
} from '../../graphql/holidays';
import HolidaysList from './HolidaysList';
import Error from '../errors/Error';
import useExtendedSubscription from '../../hooks/useExtendedSubscription';
import { showInfoNotification } from '../../actions/notifications';
import SubscriptionActions from '../../utils/SubscriptionActions';
import CreateHolidaysRow from './CreateHolidaysRow';
import { hasPrivilege } from '../../hocs/withPrivileges';
import Privileges from '../../utils/Privileges';
import useAuthentication from '../../hooks/useAuthentication';

export function createNotificationText(t, action, year) {
    let notification;
    switch (action) {
        case SubscriptionActions.HOLIDAY_CREATED:
            notification = t('holidays.notification.holidays-create-thirdparty', { year });
            break;
        case SubscriptionActions.HOLIDAY_UPDATED:
            notification = t('holidays.notification.holidays-update-thirdparty', { year });
            break;
        default:
            notification = t('holidays.notification.holidays-modified-thirdparty');
    }
    return notification;
}

function Holidays() {
    const location = useLocation();
    const { t } = useTranslation();
    const { userPrivileges } = useAuthentication();
    const { page, perPage } = parsePaging(location.search);
    const dispatch = useDispatch();
    const [refetchProps, setRefetchProps] = useState({
        perPage,
        page,
    });
    const {
        loading,
        error,
        data,
        refetch,
    } = useExtendedQuery(ALL_HOLIDAYS, {
        variables: refetchProps,
    });
    const onPageChange = (newPage) => setRefetchProps({
        ...refetchProps,
        page: newPage,
    });
    useExtendedSubscription(ON_HOLIDAY_CHANGED, {
        refetch,
        refetchVariables: refetchProps,
        dataProperty: 'holidayChanged',
        onChangeCallback: ({ action, model: { year } }) => {
            if (hasPrivilege(userPrivileges, [Privileges.HOLIDAY_EDIT])) {
                const notification = createNotificationText(t, action, year);
                dispatch(showInfoNotification(notification));
            }
        },
    });
    let result;
    let holidayProps = null;
    if (loading) {
        result = <Spinner className="holidays-loading" />;
    } else if (error) {
        result = <Error className="holidays-error" error={error} />;
    } else {
        holidayProps = {
            availableHolidayYears: data.allHolidays.availableHolidayYears,
        };
        result = (
            <>
                <CreateHolidaysRow
                    holidayProps={holidayProps}
                />
                <Paginator
                    totalCount={data.allHolidays.holidayList.totalCount}
                    perPage={perPage}
                    currentPage={refetchProps.page}
                    onPageChange={onPageChange}
                    bottom
                >
                    <HolidaysList
                        holidayYears={data.allHolidays.holidayList.models}
                        holidayProps={holidayProps}
                    />
                </Paginator>
            </>
        );
    }
    return (
        <>
            <h4><FontAwesomeIcon icon="umbrella-beach" />&nbsp;{t('title.holidays')}</h4>
            {result}
        </>
    );
}

export default Holidays;
